import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'

/**
 * useActiveNavTab
 *
 * Derives the currently active side-navigation item ID from the URL pathname.
 *
 * Canvas 8's `Layout.SideNav` accepts `isActive` as a flag on each `PrimaryMenuItem`
 * object in the `items` array. This hook resolves the current URL to an item `id`
 * so the caller can mark the right item as active.
 *
 * URL → id resolution rules (mirrors existing Layout.js logic):
 *   /project/*                 → 'projects'
 *   /annotator/*               → 'projects'
 *   /publishing/*              → 'publish'
 *   /asset-lifecycle-dashboard → 'assetLifecycleDashboard'
 *   /                          → 'home'
 *   /<segment>                 → '<segment>'
 *
 * @returns {string} The `id` of the currently active nav config item.
 */
const useActiveNavTab = () => {
  const { pathname } = useLocation()

  const activeId = useMemo(() => {
    const segment = pathname.split('/')[1] || 'home'

    if (segment === 'project' || segment === 'annotator') return 'projects'
    if (segment === 'publishing') return 'publish'
    if (segment === 'asset-lifecycle-dashboard') return 'assetLifecycleDashboard'

    return segment
  }, [pathname])

  return activeId
}

export default useActiveNavTab
